(() => {
  'use strict';

  if (window.VeloraCookieBanner) {
    window.VeloraCookieBanner.init(document);
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const instances = new Map();
  const previewMode = (root) => root.hasAttribute('data-vcb-preview');

  // The visitor's answer is also kept here (localStorage plus a first-party cookie), next to Shopify's own consent cookie.
  // If Shopify's cookie is lost between pages, the answer is put back quietly instead of asking again.
  const CHOICE_KEY = 'vcb_choice';
  const memory = { choice: null };

  const readChoice = () => {
    let raw = null;
    try { raw = window.localStorage.getItem(CHOICE_KEY); } catch (_) { /* Cookie fallback below. */ }
    if (!raw) {
      try {
        const entry = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${CHOICE_KEY}=`));
        if (entry) raw = decodeURIComponent(entry.slice(CHOICE_KEY.length + 1));
      } catch (_) { /* No stored answer. */ }
    }
    if (!raw) return memory.choice;
    try {
      const parsed = JSON.parse(raw);
      return ['a', 'm', 'p'].every((key) => parsed[key] === 0 || parsed[key] === 1) ? parsed : null;
    } catch (_) { return null; }
  };

  const writeChoice = (choice, chosenAt = Date.now(), pending = false) => {
    const stored = { a: choice.analytics ? 1 : 0, m: choice.marketing ? 1 : 0, p: choice.preferences ? 1 : 0, t: chosenAt, q: pending ? 1 : 0 };
    memory.choice = stored;
    window.__vcbChoiceAt = stored.t;
    const value = JSON.stringify(stored);
    try { window.localStorage.setItem(CHOICE_KEY, value); } catch (_) { /* Cookie below. */ }
    try { document.cookie = `${CHOICE_KEY}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax; Secure`; } catch (_) { /* In-memory copy remains. */ }
  };

  const choiceFromConsent = (current) => ({
    analytics: current.analytics === 'yes', marketing: current.marketing === 'yes', preferences: current.preferences === 'yes'
  });

  const loadPrivacyApi = () => window.__vcbPrivacy || new Promise((resolve) => {
    let settled = false;
    let retry;
    const finish = (api) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(retry);
      window.clearTimeout(timeout);
      resolve(api);
    };
    const timeout = window.setTimeout(() => finish(null), 6000);
    const attempt = () => {
      if (settled) return;
      const shopify = window.Shopify;
      if (typeof shopify?.customerPrivacy?.setTrackingConsent === 'function') return finish(shopify.customerPrivacy);
      if (typeof shopify?.loadFeatures !== 'function') {
        retry = window.setTimeout(attempt, 100);
        return;
      }
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => finish(error ? null : shopify.customerPrivacy || null));
      } catch (_) { finish(null); }
    };
    attempt();
  });

  class CookieBanner {
    constructor(root) {
      this.root = root;
      root.dataset.vcbBound = 'true';
      this.preview = previewMode(root);
      this.editor = Boolean(window.Shopify && window.Shopify.designMode);
      this.api = null;
      this.destroyed = false;
      this.listeners = [];
      this.main = root.querySelector('[data-vcb-main]');
      this.prefs = root.querySelector('[data-vcb-prefs]');
      this.on(root.querySelector('[data-vcb-accept]'), 'click', () => this.save({ analytics: true, marketing: true, preferences: true }));
      this.on(root.querySelector('[data-vcb-necessary]'), 'click', () => this.save({ analytics: false, marketing: false, preferences: false }));
      this.on(root.querySelector('[data-vcb-manage]'), 'click', () => this.showPrefs());
      this.on(root.querySelector('[data-vcb-save]'), 'click', () => this.save({
        analytics: root.querySelector('[data-vcb-analytics]').checked,
        marketing: root.querySelector('[data-vcb-marketing]').checked,
        preferences: root.querySelector('[data-vcb-preferences]').checked
      }));
      this.on(root.querySelector('[data-vcb-accept-all]'), 'click', () => this.save({ analytics: true, marketing: true, preferences: true }));
      this.on(root.querySelector('[data-vcb-back]'), 'click', () => this.showMain());
      this.on(document, 'velora:cookie-settings', () => this.reopen());
      this.on(document, 'click', (event) => { if (event.target.closest('[data-velora-cookie-settings]')) this.reopen(); });
      this.start();
    }

    on(target, type, handler) {
      if (!target) return;
      target.addEventListener(type, handler);
      this.listeners.push(() => target.removeEventListener(type, handler));
    }

    async start() {
      if (this.editor) {
        this.root.dataset.vcbReady = 'true';
        return;
      }
      if (this.preview) {
        this.root.dataset.vcbReady = 'true';
        if (params.get('state') === 'prefs') this.showPrefs();
        this.show();
        return;
      }
      if (!readChoice() || params.get('vcb') === 'force') this.show();
      this.api = await loadPrivacyApi();
      this.root.dataset.vcbReady = 'true';
      if (this.destroyed || this.choicePending) return;
      if (window.__vcbPending) {
        const pending = window.__vcbPending;
        window.__vcbPending = null;
        return this.save({ analytics: pending.a === 1, marketing: pending.m === 1, preferences: pending.p === 1 }, pending.t, true, 0);
      }
      const saved = readChoice();
      if (saved?.q === 1) {
        // A previous page may have closed before Shopify could apply the new choice.
        return this.save({ analytics: saved.a === 1, marketing: saved.m === 1, preferences: saved.p === 1 }, saved.t, false, 0);
      }
      try {
        if (!this.api) throw new Error('Privacy API unavailable.');
        const current = this.api.currentVisitorConsent();
        this.root.dataset.vcbInitialConsent = JSON.stringify(current);
        // Request a choice from new visitors; preserve an earlier yes/no choice.
        const answered = ['analytics', 'marketing', 'preferences'].every((key) => current[key] === 'yes' || current[key] === 'no');
        this.root.dataset.vcbRequired = String(!answered && !saved);
        if (answered) {
          // Keep our own copy in step with Shopify's.
          const now = choiceFromConsent(current);
          if (params.get('vcb') !== 'force') this.hide();
          if (!saved || saved.a !== (now.analytics ? 1 : 0) || saved.m !== (now.marketing ? 1 : 0) || saved.p !== (now.preferences ? 1 : 0)) { writeChoice(now); window.__vcbChoiceAt = null; }
          return;
        }
        if (saved) {
          if (params.get('vcb') !== 'force') this.hide();
          await this.restore(saved);
          return;
        }
        this.show();
      } catch (_) {
        if (saved) {
          // Answered on an earlier page, the privacy API is just not reachable now: never ask twice.
          this.root.dataset.vcbRequired = 'false';
          if (params.get('vcb') !== 'force') this.hide();
          return;
        }
        this.show();
        const error = this.root.querySelector('[data-vcb-error]');
        error.textContent = 'A süti-beállítások még nem töltődtek be. Próbáld újra a választásodat.';
        error.hidden = false;
      }
    }

    // The visitor answered on an earlier page, but Shopify no longer has the answer (cookie lost, blocked or partitioned).
    // Write it back without showing the banner. If that fails, the stored answer is simply tried again on the next page.
    async restore(saved) {
      try {
        await new Promise((resolve) => {
          const timer = window.setTimeout(resolve, 6000);
          this.api.setTrackingConsent({ analytics: saved.a === 1, marketing: saved.m === 1, preferences: saved.p === 1, sale_of_data: saved.m === 1 }, () => {
            window.clearTimeout(timer);
            resolve();
          });
        });
        this.root.dataset.vcbRestoredConsent = JSON.stringify(this.api.currentVisitorConsent());
      } catch (_) { /* Retried on the next page. */ }
    }

    show() {
      window.clearTimeout(this.hideTimer);
      if (this.editor) {
        document.querySelectorAll('[data-vsp-dialog][open]').forEach((dialog) => dialog.close());
      }
      this.root.hidden = false;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        if (this.destroyed || this.root.hidden) return;
        this.root.setAttribute('data-vcb-open', '');
        this.root.querySelector('[data-vcb-title]').focus({ preventScroll: true });
      }));
    }

    hide() {
      this.root.removeAttribute('data-vcb-open');
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => { if (!this.destroyed) this.root.hidden = true; }, 300);
    }

    reopen() {
      this.showMain();
      this.show();
    }

    showPrefs() {
      this.main.hidden = true;
      this.prefs.hidden = false;
      this.syncSwitches();
      this.root.querySelector('[data-vcb-prefs-title]').focus({ preventScroll: true });
    }

    showMain() {
      this.prefs.hidden = true;
      this.main.hidden = false;
      this.root.querySelector('[data-vcb-title]').focus({ preventScroll: true });
    }

    syncSwitches() {
      let current = null;
      try { current = this.api && typeof this.api.currentVisitorConsent === 'function' ? this.api.currentVisitorConsent() : null; } catch (_) { /* Keep defaults. */ }
      const set = (selector, key) => { this.root.querySelector(selector).checked = Boolean(current && current[key] === 'yes'); };
      set('[data-vcb-analytics]', 'analytics');
      set('[data-vcb-marketing]', 'marketing');
      set('[data-vcb-preferences]', 'preferences');
    }

    async save(choice, chosenAt = Date.now(), notify = true, retry = null) {
      if (this.saving) return;
      window.clearTimeout(this.retryTimer);
      this.saving = true;
      this.choicePending = true;
      // A tap before the API arrives must survive navigation and be applied once it is ready.
      const queued = retry !== null || (!this.api && !this.preview && !this.editor);
      if (!this.preview && !this.editor) {
        writeChoice(choice, chosenAt, true);
        if (!notify) window.__vcbChoiceAt = null;
      }
      const buttons = this.root.querySelectorAll('button');
      buttons.forEach((button) => { button.disabled = true; });
      const error = this.root.querySelector('[data-vcb-error]');
      error.hidden = true;
      const finish = () => {
        if (notify) document.dispatchEvent(new CustomEvent('velora:cookie-consent', { detail: { ...choice, chosenAt }, bubbles: true }));
        this.hide();
      };
      if (queued) finish();
      if (this.preview || this.editor) {
        finish();
      } else {
        try {
          this.api = this.api || await loadPrivacyApi();
          if (!this.api || typeof this.api.setTrackingConsent !== 'function') throw new Error('Privacy API unavailable.');
          await new Promise((resolve, reject) => {
            const timer = window.setTimeout(() => reject(new Error('Consent save timed out.')), 6000);
            this.api.setTrackingConsent({ analytics: choice.analytics, marketing: choice.marketing, preferences: choice.preferences, sale_of_data: choice.marketing }, (result) => {
              window.clearTimeout(timer);
              if (result?.error) reject(new Error('Consent save failed.'));
              else resolve();
            });
          });
          const current = this.api.currentVisitorConsent();
          if (['analytics', 'marketing', 'preferences'].some((key) => current[key] !== (choice[key] ? 'yes' : 'no'))) throw new Error('Consent not persisted.');
          this.root.dataset.vcbSavedConsent = JSON.stringify(current);
          writeChoice(choice, chosenAt);
          if (!notify) window.__vcbChoiceAt = null;
          if (!queued) finish();
        } catch (_) {
          if (queued) {
            // Keep q:1 for the next page; never reopen the banner for an early answer.
            const delays = [2000, 5000, 10000];
            const attempt = retry ?? 0;
            if (!this.destroyed && attempt < delays.length) {
              window.__vcbPrivacy = null;
              this.retryTimer = window.setTimeout(() => this.save(choice, chosenAt, false, attempt + 1), delays[attempt]);
            }
          } else {
            this.show();
            error.textContent = 'Nem sikerült menteni a választásodat. Próbáld újra.';
            error.hidden = false;
          }
        }
      }
      this.saving = false;
      this.choicePending = false;
      buttons.forEach((button) => { button.disabled = false; });
    }

    destroy() {
      this.destroyed = true;
      window.clearTimeout(this.hideTimer);
      window.clearTimeout(this.retryTimer);
      this.listeners.forEach((remove) => remove());
      instances.delete(this.root);
    }
  }

  const init = (scope) => {
    instances.forEach((instance, root) => {
      if (!root.isConnected || root.querySelector('[data-vcb-main]') !== instance.main) instance.destroy();
    });
    const roots = Array.from(scope.querySelectorAll('[data-vcb]'));
    if (scope.matches && scope.matches('[data-vcb]')) roots.unshift(scope);
    roots.forEach((root) => { if (!instances.has(root)) instances.set(root, new CookieBanner(root)); });
  };

  window.VeloraCookieBanner = { init, open: () => document.dispatchEvent(new CustomEvent('velora:cookie-settings')) };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); });
  });
  document.addEventListener('shopify:section:select', (event) => {
    init(event.target);
    instances.forEach((instance, root) => {
      if (instance.editor && event.target.contains(root)) instance.reopen();
    });
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    instances.forEach((instance, root) => {
      if (instance.editor && event.target.contains(root)) instance.hide();
    });
  });
  init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
})();
